import { useEffect, useRef } from 'react'

import type { HudStyle } from '../behaviors/types'
import type { HudBounds } from './HudOverlay'
import { outputLogTop } from './outputLogLayout'

export interface OutputLogProps {
  /** looks_showMessage（print）の出力行。再生のたびに空配列へクリアされる。 */
  lines: string[]
  /**
   * 表示中のミッション GUI（HudOverlay）のスタイル。GUI の帯と重ならない
   * top の算出に使う。GUI 非表示時は undefined。
   */
  hudStyle?: HudStyle
  /** HudOverlay の実測配置（折り返しで変わる帯の実高さを反映）。 */
  hudBounds?: HudBounds | null
}

/**
 * 「～と出力に表示する」（print）の出力ログ。Roblox Studio の Output
 * ウィンドウ相当として右上へ重ね、再生のたびにクリアされる。
 * 左上はカウント系 HUD が使うため右へ寄せ、ミッション GUI の帯とは
 * outputLogTop で縦に住み分ける（帯の下から始める）。
 */
export function OutputLog({ lines, hudStyle, hudBounds }: OutputLogProps) {
  const outputLogRef = useRef<HTMLDivElement>(null)
  // ユーザーが末尾付近を見ているときだけ、新しい出力で末尾へ自動スクロール
  // する。上へスクロールして過去の出力を遡っている間は引き戻さない。
  const outputAutoFollowRef = useRef(true)
  useEffect(() => {
    const log = outputLogRef.current
    if (log && outputAutoFollowRef.current) {
      log.scrollTop = log.scrollHeight
    }
  }, [lines])
  // 再生開始でログがクリア（lines が空に）されたら自動追従へ戻す。
  // 前回の再生で上へ遡ったままでも、新しい再生の出力は末尾を追従する。
  useEffect(() => {
    if (lines.length === 0) {
      outputAutoFollowRef.current = true
    }
  }, [lines])
  if (lines.length === 0) {
    return null
  }
  return (
    <div
      ref={outputLogRef}
      aria-label="しゅつりょく"
      onScroll={(event) => {
        // 末尾から離れたら自動追従を止め、末尾付近へ戻ったら再開する。
        const log = event.currentTarget
        outputAutoFollowRef.current =
          log.scrollHeight - log.scrollTop - log.clientHeight < 24
      }}
      style={{
        position: 'absolute',
        top: outputLogTop(hudStyle, hudBounds),
        right: 8,
        maxWidth: '55%',
        maxHeight: '40%',
        overflowY: 'auto',
        background: 'rgba(20, 24, 32, 0.72)',
        color: '#E8F0E8',
        borderRadius: 8,
        padding: '6px 10px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        // 過去の出力をスクロールで遡れるようにポインタ操作を受け付ける
        // （ログ領域上ではカメラドラッグより遡り操作を優先する）。
        pointerEvents: 'auto',
      }}
    >
      {lines.map((line, index) => (
        // 同一文言の重複行があるため index キーで安定表示する（追記のみ）。
        // eslint が無効でも並べ替えは発生しない。
        <div key={`${index}-${line}`}>{line}</div>
      ))}
    </div>
  )
}
